import React, { useState } from 'react';
import { Plus, X, ArrowLeftRight } from 'lucide-react';
import { LANGUAGES } from '../../lib/constants';

/**
 * Editor for bidirectional language pairs.
 * Props:
 *   value: Array<{source: string, target: string}>
 *   onChange: (pairs) => void
 *   testidPrefix: string
 */
const LanguagePairsInput = ({ value = [], onChange, testidPrefix = 'language-pairs' }) => {
  const [a, setA] = useState('');
  const [b, setB] = useState('');

  const normalizePair = (s, t) => {
    if (!s || !t || s.toLowerCase() === t.toLowerCase()) return null;
    const [x, y] = [s, t].sort((p, q) => p.toLowerCase().localeCompare(q.toLowerCase()));
    return { source: x, target: y };
  };

  const exists = (pair) =>
    value.some(p => p.source.toLowerCase() === pair.source.toLowerCase()
                 && p.target.toLowerCase() === pair.target.toLowerCase());

  const addPair = () => {
    const p = normalizePair(a, b);
    if (!p) return;
    if (exists(p)) {
      setA(''); setB('');
      return;
    }
    onChange([...value, p]);
    setA(''); setB('');
  };

  const removePair = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-container`}>
      {/* Existing pairs as chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-list`}>
          {value.map((p, idx) => (
            <span
              key={`${p.source}-${p.target}-${idx}`}
              className="inline-flex items-center gap-1.5 bg-[#0F4C81]/10 text-[#0F4C81] rounded-md px-2 py-1 text-sm font-medium"
              data-testid={`${testidPrefix}-item-${idx}`}
            >
              <span>{p.source}</span>
              <ArrowLeftRight className="w-3 h-3" />
              <span>{p.target}</span>
              <button
                type="button"
                onClick={() => removePair(idx)}
                className="text-[#0F4C81] hover:text-red-600 ml-1"
                data-testid={`${testidPrefix}-remove-${idx}`}
                aria-label={`${p.source} ↔ ${p.target} entfernen`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Add new pair row */}
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={a}
          onChange={(e) => setA(e.target.value)}
          data-testid={`${testidPrefix}-select-a`}
          className="border border-slate-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none min-w-[140px]"
        >
          <option value="">Sprache A…</option>
          {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <ArrowLeftRight className="w-4 h-4 text-slate-400" />
        <select
          value={b}
          onChange={(e) => setB(e.target.value)}
          data-testid={`${testidPrefix}-select-b`}
          className="border border-slate-300 rounded-md px-2 py-1.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none min-w-[140px]"
        >
          <option value="">Sprache B…</option>
          {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <button
          type="button"
          onClick={addPair}
          disabled={!a || !b || a.toLowerCase() === b.toLowerCase()}
          data-testid={`${testidPrefix}-add-btn`}
          className="inline-flex items-center gap-1 bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-3 py-1.5 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" /> Hinzufügen
        </button>
      </div>
      {value.length === 0 && (
        <p className="text-xs text-slate-500">
          Noch keine Sprachpaare. Ein Paar „Deutsch ↔ Englisch" deckt beide Richtungen ab.
        </p>
      )}
    </div>
  );
};

export default LanguagePairsInput;
